
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { motion } from 'framer-motion';
import { useCommunity } from '@/contexts/CommunityContext';

interface CommunityErrorProps {
  errorMessage?: string;
  error?: {
    title: string;
    message: string;
  };
}

const CommunityError: React.FC<CommunityErrorProps> = ({ errorMessage, error }) => {
  const { currentCommunity } = useCommunity();
  
  const handleContactSupport = () => {
    // Use admin_contact_email from community if available, otherwise fallback to default
    const contactEmail = currentCommunity?.admin_contact_email;
    window.location.href = `mailto:${contactEmail}`;
  };

  // Determine the display values based on props
  const displayTitle = error?.title || 'Community Not Found';
  const displayMessage = error?.message || errorMessage || 'An error occurred';

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        duration: 0.5,
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <motion.div 
        className="max-w-md w-full bg-white rounded-lg shadow-lg p-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div 
          className="flex justify-center mb-6"
          variants={itemVariants}
        >
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="h-8 w-8 text-red-500" />
          </div>
        </motion.div>
        
        <motion.h1 
          className="text-2xl font-bold text-center mb-4"
          variants={itemVariants}
        >
          {displayTitle}
        </motion.h1>
        
        <motion.div variants={itemVariants}>
          <Alert variant="destructive" className="mb-6">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {displayMessage}
            </AlertDescription>
          </Alert>
        </motion.div>
        
        <motion.div 
          className="space-y-4"
          variants={itemVariants}
        >
          {!error && (
            <p className="text-gray-600 text-center">
              There may be an issue with your domain configuration or the community might not exist.
            </p>
          )}
          
          <div className="flex flex-col space-y-3">
            <Button 
              onClick={handleContactSupport}
              className="w-full"
            >
              Contact Support
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => window.location.reload()}
              className="w-full"
            >
              Try Again
            </Button>
          </div>
        </motion.div>
        
        <motion.p 
          className="text-xs text-gray-500 mt-6 text-center"
          variants={itemVariants}
        >
          If you believe this is an error, please contact your administrator or our support team.
        </motion.p>
      </motion.div>
    </div>
  );
};

export default CommunityError;
